import { API_URL } from "@/constants";
import { getSessionId, peekSessionId } from "./session";
import { createQueue } from "./queue";
import { describeClick, errorPayload } from "./capture";
import { startReplay } from "./replay";

const ENDPOINT = `${API_URL}/api/telemetry`;
let queue: ReturnType<typeof createQueue> | null = null;
const teardown: (() => void)[] = [];

async function send(body: string, final: boolean): Promise<boolean> {
  const blob = new Blob([body], { type: "text/plain" });
  if (final && blob.size < 60_000 && navigator.sendBeacon) return navigator.sendBeacon(ENDPOINT, blob);
  try {
    const res = await fetch(ENDPOINT, {
      method: "POST", body: blob, credentials: "include", keepalive: blob.size < 60_000,
    });
    return res.ok;
  } catch {
    return false;
  }
}

export function sessionId(): string | null {
  return peekSessionId();
}

export function reportError(err: unknown) {
  queue?.error(errorPayload(err, location.pathname));
}

export function trackPage(url: string, title: string) {
  queue?.event({ ev: "pageview", url, title });
}

export function startTelemetry() {
  if (queue || typeof window === "undefined") return;
  const q = createQueue({ send, sid: getSessionId });
  queue = q;
  const onError = (e: ErrorEvent) => q.error(errorPayload(e.error ?? e.message, location.pathname));
  const onRejection = (e: PromiseRejectionEvent) => q.error(errorPayload(e.reason, location.pathname));
  const onClick = (e: MouseEvent) => {
    const d = e.target instanceof Element ? describeClick(e.target) : null;
    if (d) q.event({ ev: "click", url: location.pathname, el: d });
  };
  const onHide = () => { if (document.visibilityState === "hidden") void q.flush(true); };
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  document.addEventListener("click", onClick, { capture: true });
  document.addEventListener("visibilitychange", onHide);
  teardown.push(() => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
    document.removeEventListener("click", onClick, { capture: true });
    document.removeEventListener("visibilitychange", onHide);
  });
  startReplay((events) => q.replay(events)).then((stop) => teardown.push(stop)).catch(() => {});
}

export function stopTelemetry() {
  const q = queue;
  queue = null;
  for (const fn of teardown.splice(0)) fn();
  if (q) { void q.flush(true); q.stop(); }
}
